// Nuxt bakes the current build id into the client bundle and fetches
// `/_nuxt/builds/meta/<buildId>.json` (the app manifest) during app init. After a
// redeploy the previous build's meta file is gone, so a still-open tab requests a
// buildId that now 404s:
//   FetchError: [GET] "/_nuxt/builds/meta/<id>.json": 404
//   [nuxt] Error fetching app manifest
// It surfaces as an unhandled promise rejection ("error caught during app
// initialization") and floods Sentry, but it is not actionable in code — the tab
// simply holds a stale build. The only recovery is a hard reload to fetch the
// current index (which carries the new buildId, so the manifest then resolves).
//
// A short sessionStorage cooldown prevents a reload loop if the fresh build still
// cannot be fetched (mid-deploy / CDN purge in flight): after one attempt we let the
// rejection surface normally instead of hammering the server.
const RELOAD_COOLDOWN_MS = 10_000;
const RELOAD_MARKER_KEY = "stale-build-reload:last-attempt";

// True when the rejection reason is the stale app-manifest 404 described above.
export function isStaleBuildManifestError(reason: unknown): boolean {
  const message =
    typeof reason === "string"
      ? reason
      : reason instanceof Error
        ? reason.message
        : "";

  return message.includes("/_nuxt/builds/meta/") && message.includes("404");
}

// True when enough time has passed since the last automatic reload to warrant a fresh
// attempt (or when we have never reloaded this session).
export function shouldReloadForStaleBuild(now: number, lastAttempt: number | null): boolean {
  if (lastAttempt === null) return true;
  return now - lastAttempt > RELOAD_COOLDOWN_MS;
}

export default defineNuxtPlugin(() => {
  if (typeof window === "undefined") return;

  window.addEventListener("unhandledrejection", (event) => {
    if (!isStaleBuildManifestError(event.reason)) return;

    // Swallow the noise: nothing downstream can act on a stale-build 404.
    event.preventDefault();

    let lastAttempt: number | null = null;
    try {
      const stored = window.sessionStorage.getItem(RELOAD_MARKER_KEY);
      lastAttempt = stored ? Number(stored) : null;
      if (lastAttempt !== null && Number.isNaN(lastAttempt)) lastAttempt = null;
    } catch {
      // sessionStorage can throw in private-mode / sandboxed contexts — treat as "no prior attempt".
      lastAttempt = null;
    }

    if (!shouldReloadForStaleBuild(Date.now(), lastAttempt)) return;

    try {
      window.sessionStorage.setItem(RELOAD_MARKER_KEY, String(Date.now()));
    } catch {
      // Ignore — a failed write only means we lose the loop guard, not correctness.
    }

    window.location.reload();
  });
});
